import { AppTimestampContext } from "global/cnr/contexts/AppTimestampContext";
import { TimestampsContext } from "global/cnr/contexts/TimestampsContext";
import { shouldUseCache } from "global/cnr/contexts/useHelpers/useHelpers";
import { createRefPath_event } from "global/firestoreData/appData/appRefPaths";
import _ from 'lodash';
import { useCallback, useContext, useState } from "react";
import { _showAppLogs } from "../../../global/cnr/contexts/LogContext";
import { useCacheDataManager } from "../../../global/cnr/contexts/useHelpers/useCacheDataManager";
import { useMultiDoc } from "../../../global/cnr/contexts/useHelpers/useMultiDoc";
import { _cacheGroupTypes } from "../../../global/cnr/contexts/useHelpers/useTimestamp";
import { seasonalHelpers } from "../cnr/reducers/reducerHelpers/seasonalHelpers";
import { _sportsInfoTypes } from "./useSportsInfo";
import { doc, setDoc, writeBatch } from "firebase/firestore";
import { ParentContext } from "global/cnr/contexts/ParentContext";
import { _resultsSeasonalCollection } from "../cnr/reducers/SportsSeasonReducer";

export const _allowResultsUpdate = true

const _applyCache = true

const dataItems_sportsSeasonResults = {
  results_currentSeason: {
    combineSubs: true,
    renderAs: 'results',
    collectionName: _resultsSeasonalCollection,
    ignoreCache: true,
    sportPropName: 'seasonResults',
    collectionSubs: ['results', 'playoffResults'],
    _subKey: 'matches',
  },
}

export const useSportResults = ({ sportsInfoType, pathViews }) => {

  const parentContext = useContext(ParentContext);
  const { states, fns } = parentContext ?? {}
  const { app_state } = states ?? {}
  const { database_fns } = fns
  const { mainDb } = app_state ?? {}

  const cache_groupType = _cacheGroupTypes.sportsSeasonResultsInfo;

  // timestampsContext
  const timestampsContext = useContext(TimestampsContext)
  const { timestamps_state } = timestampsContext ?? {}
  const { _validatedTimestamps } = timestamps_state ?? {}

  const [updating, setUpdating] = useState()

  const { handleGet_multiDocs_noListen } = useMultiDoc()
  const cdm = useCacheDataManager()

  const handle_sportsMatchesInfo = async ({ sports_key, sportsResultsData, pdks, callback, fromCache, newTimestamp }) => {
    const sportsSeason_datas = seasonalHelpers.ammend_sportsSeasonDataz(sportsResultsData, '2026')
    sportsSeason_datas.parentDataKeys = pdks
    sportsSeason_datas._validatedTimestamp = newTimestamp
    _showAppLogs.sportInfo && console.log(`✅${fromCache ? '🗄️' : '🔥'}${sportsInfoType}`, sportsSeason_datas)
    if (!fromCache) {
      const fullData = { sportsResultsData }
      await cdm.mergeToGroup(cache_groupType, sports_key, fullData, newTimestamp, true);
    }
    callback(sportsSeason_datas)
  }

  /**
   * handles the returned data from _refPath_s
   * @param {object} sport 
   */
  const handleDbData_sportsSeasonMatches = (sports_key, sport, cb_main, ts) => {
    const cb = async (sportsResultsData) => handle_sportsMatchesInfo({ sports_key, sportsResultsData, callback: cb_main, fromCache: false, newTimestamp: ts })
    const { latestSeason } = sport ?? {}
    const _pks = seasonalHelpers.getPks(pathViews, latestSeason, sports_key, ['teams'])
    handleGet_multiDocs_noListen({
      key: 'sportsResults',
      sportsDocs: dataItems_sportsSeasonResults,
      pathViews,
      itemKey: sports_key,
      pks: _pks,
      callback: cb
    })
  }

  const getSportsResults = useCallback(async (_sportsInfoType, sports_key, callback, ts_ssri) => {

    const cgt = _sportsInfoType + 'Info';
    const currentTimestamp_db = ts_ssri // timestamps?.[cgt]?.[pathViews.sports];

    const cb_data = (data) => {
      switch (_sportsInfoType) {
        case _sportsInfoTypes.sportsSeasonResults:
          handleDbData_sportsSeasonMatches(sports_key, data, callback, currentTimestamp_db)
      }
    }

    const cb_cache = (cachedData) => {
      // fromCache and newTimestamp included in cachedData
      switch (_sportsInfoType) {
        case _sportsInfoTypes.sportsSeasonResults:
          handle_sportsMatchesInfo({ ...cachedData, sports_key, callback, fromCache: true })
      }
    }

    switch (_sportsInfoType) {
      case _sportsInfoTypes.sportsSeasonResults:
        getResultsItemData(cdm, currentTimestamp_db, cgt, pathViews, sports_key, 'sports', true, database_fns, cb_cache, cb_data, _validatedTimestamps)
    }
  }, [])


  const createSeasonalResults = useCallback(async ({ pathViews, seasonalKey, parentKeys, results, forPlayoffs }) => {
    setUpdating(true)
    const cb = () => setUpdating(false)
    createResultsSeasonal({ mainDb, pathViews, seasonalKey, parentKeys, results, forPlayoffs, callback: cb })
  }, [])

  return { getSportsResults, createSeasonalResults, updating }

};

const getResultsItemData = async (
  cdm,
  currentTimestamp_db,
  cgt,
  pathViews,
  sportsKey,
  collectionName,
  ignoreId,
  database_fns,
  callback_cache,
  callback_data,
  _validatedTimestamps
) => {

  // get the cache
  const cache_result = await cdm.getSubItem(cgt, sportsKey);

  const { _timestamp: timestamp_cache } = cache_result ?? {}

  // KEY GUARD: already have this version 
  const validatedTimestamp = _validatedTimestamps?.[cgt] ?? {};

  if (validatedTimestamp && validatedTimestamp === currentTimestamp_db) {
    _showAppLogs.sportInfo && console.log(`✅${cgt}`)
    // return; // 🚫 do nothing
  }

  const allowCache = shouldUseCache(timestamp_cache, currentTimestamp_db, cgt)

  // return the cache data if available
  if (_applyCache && !_.isEmpty(cache_result) && allowCache) {
    callback_cache({ ...cache_result, fromCache: true, newTimestamp: currentTimestamp_db })
    return;
  }

  // get the data from the database
  const _refPath = createRefPath_event(pathViews, [collectionName, sportsKey])
  database_fns.get_doc({
    refPath: _refPath,
    opts: { ignoreId: ignoreId },
    callback: callback_data
  })
}

const createResultsSeasonal = async ({ mainDb, pathViews, seasonalKey, parentKeys, results, forPlayoffs, callback }) => {

  // ---------------------------------------------------------
  // Results seasonal parent document
  // ---------------------------------------------------------

  const refPath_results = createRefPath_event(pathViews, ['_results_seasonal', seasonalKey])
  const ref_results = doc(mainDb, refPath_results)

  const resultsData = { parentKeys: parentKeys || {} }

  if (_allowResultsUpdate) {
    // Parent document contains ONLY parentKeys
    await setDoc(ref_results, resultsData)
  }

  // ---------------------------------------------------------
  // Results group documents
  // ---------------------------------------------------------

  const batch = writeBatch(mainDb);

  _.forEach(results, (result, groupKey) => {

    const { matches } = result

    // parentKeys is not a results group
    if (groupKey === 'parentKeys') return

    // Nothing to write for this group
    if (_.isEmpty(result)) return

    const docName = forPlayoffs ? 'playoffResults' : 'results'

    const refPath_result = createRefPath_event(pathViews, ['_results_seasonal', seasonalKey, docName, groupKey])
    const ref_result = doc(mainDb, refPath_result)

    batch.set(ref_result, {
      matches
    })

  })

  if (_allowResultsUpdate) {
    await batch.commit()
  }

  callback()

  return {
    success: true,
    seasonalKey
  }
}